import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateMain } from '@hero/core';
import {
  OrderBookedIcon,
  OrderConfirmedIcon,
  ArrivedIcon,
  PickedUpIcon,
  DropOffIcon,
  CompletedIcon,
  CanceledIcon,
  StartJourneyIcon,
} from '@hero/web-icons';
import { TimelineStep } from '@hero/hooks-shared';
import { TimelineNameId } from '@hero/core';

export interface TimelineStepProps extends TimelineStep {
  orderRef: string;
  isFirst: boolean;
}

interface StepConfig {
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  activeLabel?: string;
}

const STEP_CONFIGS: Record<TimelineNameId, StepConfig> = {
  order_created: {
    icon: OrderBookedIcon,
    labelKey: 'timeline.load_booked',
  },
  customer_confirmation: {
    icon: OrderConfirmedIcon,
    labelKey: 'timeline.order_confirmed',
    activeLabel: 'timeline.awaiting_confirmation',
  },
  start_journey: {
    icon: StartJourneyIcon,
    labelKey: 'timeline.journey_started',
    activeLabel: 'timeline.ready_to_start',
  },
  in_transit_to_pickup: {
    icon: ArrivedIcon,
    labelKey: 'timeline.arrived_at_pickup',
    activeLabel: 'timeline.heading_to_pickup',
  },
  items_picked_up: {
    icon: PickedUpIcon,
    labelKey: 'timeline.picked_up',
    activeLabel: 'timeline.loading_items',
  },
  in_transit_to_dropoff: {
    icon: ArrivedIcon,
    labelKey: 'timeline.arrived_at_dropoff',
    activeLabel: 'timeline.heading_to_dropoff',
  },
  items_dropped_off: {
    icon: DropOffIcon,
    labelKey: 'timeline.dropoff',
    activeLabel: 'timeline.unloading_items',
  },
  completion: {
    icon: CompletedIcon,
    labelKey: 'timeline.completed',
  },
  cancelation: {
    icon: CanceledIcon,
    labelKey: 'timeline.canceled',
  },
  order_booked_by_carrier: {
    icon: OrderBookedIcon,
    labelKey: 'timeline.carrier_assigned',
  },
};

export const TimelineStepComponent: React.FC<TimelineStepProps> = ({
  stepId,
  status,
  occurred,
  inProgress,
  date,
  orderRef,
  isFirst,
}) => {
  const { t } = useTranslation('m-order');

  const config = STEP_CONFIGS[stepId];
  const isActive = status === 'in_progress';
  const isUpcoming = status === 'upcoming';

  const Icon = config.icon;

  // Simplified color scheme:
  // - Occurred: Emerald (vibrant green)
  // - Active: Amber (with animation)
  // - Upcoming: Gray
  // - Canceled: Red (special case)
  const bgColorClass = (() => {
    if (stepId === 'cancelation') return 'bg-error-300'; // Red for canceled
    if (isActive) return 'bg-amber-400'; // Amber for active
    if (occurred) return 'bg-emerald-500'; // Emerald green for occurred
    return 'bg-gray-300'; // Gray for upcoming
  })();

  // Get label
  const getLabel = () => {
    if (isFirst) {
      return t(config.labelKey as never, { number: orderRef });
    }
    if (isActive && config.activeLabel) {
      return t(config.activeLabel as never);
    }
    return t(config.labelKey as never);
  };

  const label = getLabel();

  return (
    <div className="flex flex-row items-center justify-between">
      <div className="flex flex-row items-center gap-3">
        <div className="relative">
          {isActive && (
            <>
              <div className="absolute inset-[-5px]">
                <div
                  className="w-full h-full rounded-full"
                  style={{
                    background:
                      'conic-gradient(from 0deg, transparent 0%, #60a5fa 25%, #3b82f6 50%, #2563eb 75%, transparent 100%)',
                    animation: 'spin 1.5s linear infinite',
                    WebkitMaskImage: 'radial-gradient(transparent 50%, black 50%)',
                    maskImage: 'radial-gradient(transparent 50%, black 50%)',
                  }}
                />
              </div>
              <div className="absolute inset-[-5px] rounded-full border border-blue-200/30" />
            </>
          )}
          <div
            className={`z-10 relative w-[34px] h-[34px] ${bgColorClass} rounded-full flex justify-center items-center`}>
            <Icon className={`w-5 h-5 ${isUpcoming ? 'text-gray-500' : 'text-white'}`} />
          </div>
        </div>
        <p className={`subtitle2 ${isUpcoming ? 'text-gray-500' : 'text-black'}`}>{label}</p>
      </div>
      <div className="flex flex-row gap-1">
        <p className={`body2 ${isUpcoming ? 'text-gray-500' : 'text-gray-600'}`}>
          {date
            ? formatDateMain({
                date: date,
                format: 'date_day_time',
              })
            : ''}
        </p>
      </div>
    </div>
  );
};
